import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import { FormlyFormOptions, FormlyModule } from "@ngx-formly/core";
import _ from "lodash";
import { BlockUI, BlockUIModule, NgBlockUI } from "ng-block-ui";
import { MessageService } from "primeng/api";
import {
  Observable,
  catchError,
  interval,
  last,
  map,
  of,
  switchMap,
  takeWhile,
} from "rxjs";
import { FileUploadComponent } from "../../../components/common/file-upload/file-upload.component";
import { LoadingComponent } from "../../../components/common/loading/loading.component";
import { VersioningSelectComponent } from "../../../components/common/versioning-select/versioning-select.component";
import { ImpactedGenerateComponent } from "../../../components/modal-templates/impacted-generate/impacted-generate.component";
import { SharedComponents } from "../../../components/shared.module";
import {
  generateMappingTemplate,
  generateMappingTemplateEdit,
  generateMappingTemplateImpacted,
} from "../../../core/generic-templates/generate-mapping";
import { TestPrioritizationActions } from "../../../core/ngrx/actions/test-prioritization.action";
import { selectConnections } from "../../../core/ngrx/selectors/connection.selectors";
import {
  selectMappingResponse,
  selectTestCasesToSourceMapping,
  selectTestPrioritizationIsMappingCreated,
  selectTestPrioritizationLoading,
} from "../../../core/ngrx/selectors/test-prioritization.selectors";
import { selectAllTestCaseVersions } from "../../../core/ngrx/selectors/testcase-generator.selectors";
import { BreadcrumbService } from "../../../core/services/breadcrumb.service";
import { ProjectService } from "../../../core/services/project/project.service";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { MaterialModule } from "../../../core/shared/material.module";
import { MessageEnum } from "../../../core/utils/message.enum";

/**
 * This component handles the creation, editing, and management of mappings between test cases and source code.
 * It provides a dynamic form to accept user inputs, manages API interactions, and displays progress statuses.
 */
@Component({
  selector: "app-generate-mapping",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyBootstrapModule,
    VersioningSelectComponent,
    LoadingComponent,
    BlockUIModule,
    FileUploadComponent,
    MaterialModule,
    LoadingComponent,
    ...SharedComponents,
  ],
  templateUrl: "./generate-mapping.component.html",
  styleUrl: "./generate-mapping.component.scss",
})
export class GenerateMappingComponent {
  @Input()
  data: any;

  @Input()
  isEdit: boolean = false;

  @Input()
  selectedMode: "impacted" | "most-impacted" | "" = "";

  @Output() eventHandler = new EventEmitter();
  @Output() moveToNextTabEvent = new EventEmitter<void>();
  @Output() enableTabsEvent = new EventEmitter<number[]>();

  private messageService = inject(MessageService);
  fileUploadConfig = {
    fileUploadLabel: "Upload Test Cases",
    fileTypes: ".csv",
    buttonLabel: "Choose file",
    placeholderText: "CSV files only",
  };

  fileUploadConfigMostImpacted = {
    fileUploadLabel: "Defect Report *",
    fileTypes: ".csv",
    buttonLabel: "Choose file",
    placeholderText: "CSV upto 20MB",
  };

  progressStatus: any = {
    text: "Initializing",
    percentage: 3,
  };

  completionStatus: any = [];
  isValidating: boolean = false;

  @BlockUI()
  blockUI!: NgBlockUI;

  testCaseVersions$!: Observable<any[]>;
  loading$: Observable<boolean> = this.store.select(
    selectTestPrioritizationLoading
  );
  isMappingCreated$: Observable<boolean> = this.store.select(
    selectTestPrioritizationIsMappingCreated
  );
  mappingResponse$ = this.store.select(selectMappingResponse);
  sourceCodeMappingTestCases$: Observable<any[]> = this.store.select(
    selectTestCasesToSourceMapping
  );
  displayPreviewBtn = false;
  fields: any;
  form = new FormGroup({});
  model: any = {
    LLM: "",
    vectorStoreApiKey: "",
    githubLink: "",
    githubToken: "",
    vectorDatabase: "",
    embeddingModel: "",
    languageModel: "",
  };
  options: FormlyFormOptions = {};
  processStatus: any[string] = [];
  isGenerating = false;
  isMappingUpdating = false;
  isMappingUpdated = false;
  projectId: any = "";
  mappingName = "";
  isImpactedTestcasesUpdating = false;
  uploadedFileName: string = "";
  // Local contants
  private localConstants: any = {
    TESTCASE_TO_MAPPING: "TestCase to Source Code Mappings",
    CODE_COVERAGE: "Code Coverage",
    GET_RECOMMENDATIONS: "Get Recommendations",
    IMPACTED_TESTCASES: "Impacted TestCases",
    TESTCASE_TO_MAPPING_MSG: "Mappings have been created.",
    CODE_COVERAGE_MSG: "Code coverage has been calculated.",
    GET_RECOMMENDATIONS_MSG: "The Recommendations have been generated.",
  };
  mappingId: any;
  files: any = [];
  isCreateMapping: boolean = false;

  isGenerateEnable: boolean = false;
  formData: any;
  viewType: "summary" | "impacted" | "most-impacted" = "summary";
  connectionsList: any;
  showLoader = false;

  private modalService = inject(NgbModal);
  mappings: any = [];

  /**
   * Initializes the component with required services.
   * @param {ProjectService} projectIdService - Service for fetching project-related data.
   * @param {Store} store - NgRx store for state management.
   * @param {UtilitiesService} utilitiesService - Utility service for common operations.
   * @param {RestService} restService - Service for executing API calls.
   */
  constructor(
    private projectIdService: ProjectService,
    private store: Store,
    private utilitiService: UtilitiesService,
    private restService: RestService,
    public breadcrumbService: BreadcrumbService,
    private cdRef: ChangeDetectorRef,
    private readonly toaster: ToasterService
  ) {}

  /**
   * Lifecycle hook that initializes the component and subscribes to observables.
   */
  ngOnInit() {
    this.store.select(selectConnections).subscribe((connections: any) => {
      const embeddingConnectionList = connections?.LLM?.filter((item: any) => item.hasEmbeddedModel);
      this.connectionsList = embeddingConnectionList?.map((connection: any) => ({
        label: connection?.connectionName,
        value: connection?.id,
      }));

      const llmField = generateMappingTemplate
        .flatMap((group) => group.fieldGroup)
        .find((field) => field.key === "LLM");

      if (llmField) {
        llmField.templateOptions.options = this.connectionsList;
      }
      const llmFieldEdit = generateMappingTemplateEdit
        .flatMap((group) => group.fieldGroup)
        .find((field) => field.key === "LLM");

      if (llmFieldEdit) {
        llmFieldEdit.templateOptions.options = this.connectionsList;
      }

      console.log("llm", llmField);

      this.fields = this.isEdit
        ? _.cloneDeep(generateMappingTemplateEdit)
        : _.cloneDeep(generateMappingTemplate);
      if (!this.isEdit) {
        this.model = {
          ...this.data,
        };
      }
    });

    if (this.selectedMode) {
      this.handleTestCaseButtonClick(this.selectedMode, true);
    }

    this.projectIdService.getProjectId().subscribe((id) => {
      this.projectId = id;
      this.loadImpactfulTestCases();
    });
    this.testCaseVersions$ = this.store.select(selectAllTestCaseVersions).pipe(
      map((connections) =>
        connections?.map((connection: any) => ({
          value: connection,
          label: connection.versionName,
        }))
      )
    );

    this.store.select(selectTestCasesToSourceMapping).subscribe((item) => {
      if (item?.length) {
        this.completionStatus.push({
          message: "Source Code Mapping created successfully",
        });
        this.completeProgress();
      }
    });

    // this.utilitiService.dispatchIfEmpty(
    //   this.testCaseVersions$,
    //   TestCaseGeneratorActions.loadTestCaseVersions({
    //     projectId: this.projectId,
    //   })
    // );

    // Dynamically update options for the 'testCaseVersion' field
    // this.testCaseVersions$.subscribe((versions) => {
    //   this.updateFieldOptions(
    //     this.fields.createMapping,
    //     "testCaseVersion",
    //     versions
    //   );
    //   if (this.data) {
    //     let selectedVersion = versions.find(
    //       (version: any) =>
    //         version?.value?._id === this.model.testCasesVersionId
    //     );
    //     this.model.testCaseVersion = selectedVersion?.value;
    //   }
    // });

    this.mappingResponse$.subscribe((response) => {
      if (response) {
        this.mappingId = response.id;
        this.mappingName = response?.name;
        if (this.isEdit) this.model = { ...response, LLM: "gpt_4.1" };
        if (this.isCreateMapping) {
          this.completionStatus.push({
            message: "Mapping created successfully",
          });
          this.excelFileUpload();
        }
      }
    });
  }

  loadImpactfulTestCases() {
    this.restService.getImpactfulTestCases(this.projectId).subscribe({
      next: (response) => {
        this.mappings = response?.data;
      },
      error: (err) => {
        console.error("Error fetching impactful test cases:", err);
      }
    });
  }

  excelFileUpload() {
    const testCaseFormdata = new FormData();
    if (this.files?.length) {
      testCaseFormdata.append("file", this.files[0], this.files[0]?.name);
    }
    this.restService
      .testCaseFileUpload(this.mappingId, testCaseFormdata)
      .subscribe(
        (response: any) => {
          if (response?.status === 201) {
            this.codeSnippedGenerate();
          }
        },
        (error) => {
          this.toaster.pop({ type: 'error', text: MessageEnum.FILE_UPLOAD_FAILURE });
          this.isGenerating = false;
          this.completionStatus = [];
          this.deleteMappingOnErr();
        }
      );
  }

  codeSnippedGenerate() {
    this.restService
      .createCodeSnipped(this.mappingId)
      .pipe(
        switchMap((response: any) => {
          if (response?.status === 201) {
            const asyncToken = response?.asyncToken;
            console.log("Code Snippet ID:", asyncToken);

            return interval(20000).pipe(
              switchMap(() =>
                this.restService.handleAsyncRequest(asyncToken).pipe(
                  map((statusResponse: any) => {
                    this.progressStatus.percentage += 3;
                    if (statusResponse?.asyncReqStatus === "Success") {
                      return {
                        success: true,
                        codeSnipped: statusResponse?.data?.codeSnipped,
                      };
                    }
                    if (statusResponse?.asyncReqStatus === "Failed") {
                      this.toaster.pop({ type: 'error', text: MessageEnum.CODE_SNIPPET_GENERATION_FAILURE });
                      throw new Error("Code Snippet generation failed.");
                    }
                    return { success: false };
                  })
                )
              ),
              takeWhile((result) => !result.success, true),
              last()
            );
          } else {
            throw new Error("Initial request failed.");
          }
        }),
        catchError((error) => {
          this.toaster.pop({ type: 'error', text: error.message || MessageEnum.CODE_SNIPPET_GENERATION_FAILURE });
          this.deleteMappingOnErr();
          this.resetToDeafultView();
          return of(null);
        })
      )
      .subscribe((finalResult) => {
        if (finalResult && finalResult.success) {
          console.log("Final Recommendations:", finalResult.codeSnipped);
          this.progressStatus.percentage += 5;
          this.completionStatus.push({
            message: "Source code snippet generated successfully",
          });
          //this.completeProgress();
          this.sourceCodeMapping();
        }
      });
  }

  /**
   * Dynamically updates the options for a form field.
   * @param {any[]} fields - Array of form fields.
   * @param {string} key - Key of the field to update.
   * @param {any[]} options - New options to set.
   */
  updateFieldOptions(fields: any[], key: string, options: any[]) {
    if (!Array.isArray(fields)) {
      return;
    }

    for (let field of fields) {
      if (field.key === key) {
        field.templateOptions.options = options; // Update options dynamically
        return;
      } else if (field.fieldGroup) {
        this.updateFieldOptions(field.fieldGroup, key, options); // Recursive search
      }
    }
  }

  /**
   * Handle action on button 'Proceed' click
   */
  onProceed() {
    if(this.mappings?.find((item: any) => item.name.toLowerCase() === this.mappingName.toLowerCase())) {
      this.toaster.pop({ type: 'error', text: MessageEnum.MAPPING_NAME_ALREADY_EXISTS });
      return;
    }
    this.isValidating = true;
    const formdata = new FormData();
    console.log('LLM',  this.model);

    formdata.append("githubLink", this.model?.githubLink);
    formdata.append("githubToken", this.model?.githubToken);

    this.restService.validateGit(formdata).subscribe({
      next: (response) => {
        this.isValidating = false;
        this.isCreateMapping = true;
        const payload = this.preparePayload(this.files);
        this.isGenerating = true;
        this.progressStatus.percentage = 3;
        this.progressStatus.text = 'Initializing';

        setTimeout(() => {
          this.progressStatus.percentage = 20;
          this.progressStatus.text = `Generating mapping`;
        }, 3000);
        this.store.dispatch(
          TestPrioritizationActions.createMapping({ payload })
        );
        this.toaster.pop({ type: 'success', text: MessageEnum.GIT_VALIDATE_SUCCESS });
      },
      error: (err) => {
        this.isValidating = false;
        var errorMsg = err?.message
          ? err?.message
          : MessageEnum.GIT_VALIDATE_FAILURE;
          this.toaster.pop({ type: 'error', text: errorMsg });
        this.isGenerating = false;
        this.completionStatus = [];
      },
    });
  }

  deleteMappingOnErr() {
    this.restService.deleteMapping(this.mappingId).subscribe((res: any) => {
      if(res) {
        console.log('Mapping deleted successfully!');
      }
    });
  }

  preparePayload(data: any): FormData {
    const formdata = new FormData();

    // Add other fields to the FormData
    formdata.append("projectId", this.projectId);
    formdata.append("name", this.mappingName);
    formdata.append("vectorDatabase", this.model?.vectorDatabase);
    formdata.append("vectorStoreApiKey", this.model?.vectorStoreApiKey);
    formdata.append("githubLink", this.model?.githubLink);
    formdata.append("githubToken", this.model?.githubToken);
    formdata.append("synopsis", this.model?.synopsis);
    formdata.append("embeddingSourceConnection", this.model?.LLM);
    formdata.append("testCasesVersionName", "dddd");
    if (this.isEdit) {
      formdata.append("id", this.mappingId);
      const emptyFile = new File([""], "empty.txt", { type: "text/plain" });
      formdata.append("file", emptyFile);
    }

    return formdata;
  }

  /**
   * Generates source code mappings and manages progress status updates.
   */
  sourceCodeMapping() {
    this.progressStatus.text = "Generating source code mapping";
    this.progressStatus.percentage += 5;
    this.isCreateMapping = false;
    interval(15000)
      .pipe(takeWhile(() => this.progressStatus.percentage < 97))
      .subscribe(() => {
        this.progressStatus.percentage += 3;
      });
    this.store.dispatch(
      TestPrioritizationActions.loadTestCases({
        mappingId: this.mappingId,
      })
    );

    // this.completeProgress();
  }

  resetToDeafultView() {
    this.progressStatus.percentage = 3;
    this.progressStatus.text = "Initializing";
    this.completionStatus = [];
    this.isGenerating = false;
  }

  /**
   * Fetches and displays the impacted test cases based on the mapping ID and GitHub link.
   * Sets a loading state while the operation is in progress and emits an event upon completion.
   */
  viewImpacted(model: any, isMostImpacted = true) {
    this.showLoader = true;
    if (isMostImpacted) {
      if (model?.files?.length > 0) {
        this.formData = new FormData();
        this.isImpactedTestcasesUpdating = true;
        const sanitizedFileName = model.files[0]?.name.replace(/\s+/g, "_"); // Replace spaces with underscores
        console.log("fileName->", sanitizedFileName);
        this.formData.append("file", model.files[0], sanitizedFileName);
        // this.formData.append("file", this.files[0], this.files[0]?.name);

        this.restService
          .uploadDefectReport(this.mappingId, this.formData)
          .subscribe(
            (response) => {
              this.uploadedFileName = response?.data?.defect_report_name
                ? response?.data?.defect_report_name
                : "";
              this.restService
                  .generatedImpactedTestcases(model.id, model.githubLink)
                  .subscribe(
                    (response) => {
                      this.showLoader = false;
                      this.isImpactedTestcasesUpdating = false;
                      this.eventHandler.emit({
                        type: "view_most_impacted",
                        data: {
                          file: this.uploadedFileName,
                        },
                      });
                    },
                    (error) => {
                      this.showLoader = false;
                      this.toaster.pop({ type: 'error', text: MessageEnum.IMPACTED_TESTCASES_GENERATION_FAILURE });
                      this.isImpactedTestcasesUpdating = false;
                    }
                  );
            },
            (error) => {
              this.showLoader = false;
              this.toaster.pop({ type: 'error', text: MessageEnum.FILE_UPLOAD_FAILURE });
              this.isImpactedTestcasesUpdating = false;
            }
          );
      } else {
        this.showLoader = false;
        this.toaster.pop({ type: 'error', text: MessageEnum.DEFECT_REPORT_UPLOAD_REQUIRED });
      }
    } else {
      this.isImpactedTestcasesUpdating = true;
      this.restService
        .generatedImpactedTestcases(model.id, model.githubLink)
        .subscribe(
          (response) => {
            this.showLoader = false;
            this.isImpactedTestcasesUpdating = false;
            this.eventHandler.emit({
              type: "view_impacted",
            });
          },
          (error) => {
            this.showLoader = false;
            const errorMessage =
              error?.error?.message ||
              MessageEnum.IMPACTED_TESTCASES_LOAD_FAILURE;
            this.isImpactedTestcasesUpdating = false;
            this.toaster.pop({ type: 'error', text: errorMessage });
          }
        );
    }
  }

  /**
   * Updates the source code mapping with the selected files.
   * Prepares the payload for the update request, handles success and error responses,
   * and displays a corresponding message.
   */
  sourceCodeMappingEdit() {
    this.isMappingUpdating = true;
    const payload = this.preparePayload(this.files);
    this.restService.updateMapping(payload).subscribe(
      (item) => {
        this.model = item?.data;
        this.toaster.pop({ type: 'success', text: MessageEnum.TESTCASE_CODE_MAPPING_UPDATE_SUCCESS });
        this.isMappingUpdating = false;
        this.isMappingUpdated = true;
      },
      (error) => {
        this.toaster.pop({ type: 'error', text: MessageEnum.TESTCASE_CODE_MAPPING_UPDATE_FAILURE });
        this.isMappingUpdating = false;
        this.isMappingUpdated = false;
      }
    );
  }

  /**
   * Handles file selection by storing the selected files and updating the provided data object.
   *
   * @param event - The file selection event containing the selected files.
   * @param data - The data object to which the selected files are assigned.
   */
  handleFilesSelected(event: any, data: any) {
    this.files = event.files;
    data.selectedFiles = this.files;
  }

  /**
   * Completes the progress process by setting the progress to 100% and disabling the generating state.
   * Emits events to enable specific tabs and moves to the next tab after a short delay.
   */
  completeProgress() {
    this.progressStatus.text = "";
    this.progressStatus.percentage = 100;
    setTimeout(() => {
      this.isGenerating = false;
      this.enableTabsEvent.emit([1]);
      this.enableTabsEvent.emit([2]);
      this.enableTabsEvent.emit([3]);
      this.moveToNextTabEvent.emit();
    }, 4000);
  }

  handleTestCaseButtonClick(
    type: "impacted" | "most-impacted",
    skipBreadcrumb = false
  ) {
    this.openDetailedModal(type === "impacted");
    return;
  }

  cancel() {
    this.breadcrumbService.deleteNode({
      name:
        this.viewType === "impacted"
          ? "Impacted Test Cases"
          : "Most Impacted Test Cases",
      key: "analysisType",
    });
    this.viewType = "summary";
    this.isGenerateEnable = false;
    this.fields = _.cloneDeep(generateMappingTemplateEdit);
    this.cdRef.detectChanges(); // Force UI update
  }

  isValid() {
    return this.form.valid;
  }

  openDetailedModal(isImpacted = false) {
    const llmField = generateMappingTemplateImpacted
      .flatMap((group) => group.fieldGroup)
      .find((field) => field.key === "LLM");

    if (llmField) {
      llmField.templateOptions.options = this.connectionsList;
    }

    const modalRef = this.modalService.open(ImpactedGenerateComponent, {
      centered: true,
      windowClass: "impacted-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.enableFileUpload = !isImpacted;
    modalRef.componentInstance.model = this.model;
    modalRef.componentInstance.fields = _.cloneDeep(
      generateMappingTemplateImpacted
    );
    modalRef.componentInstance.title = isImpacted
      ? "Impacted Test Cases"
      : "Most Impcated Test Cases";
    modalRef.result.then((result: any) => {
      this.viewImpacted(result, !isImpacted);
    });
  }
}
